import { ReactNode } from 'react';

import { Box } from '@mui/material';

import { OfferCategory } from '../../../../graphql/API';
import { BrandPlan, LogicCondition, Plan } from '../../types';

type Data = {
  titleText: string;
  title: ReactNode;
  highlight?: ReactNode;
  plan: string;
  benefitTitle: ReactNode;
  benefitColor?: string;
  benefits: { title: string | ReactNode; description?: string; tooltip?: string }[];
  tips?: string;
};

// Venue type based reservation limits
type VenueTypeReservationLimits = {
  basic: string;
  starter: string;
  plus: string;
};

const venueTypeReservationLimits: Record<string, VenueTypeReservationLimits> = {
  [OfferCategory.beauty]: {
    basic: 'Up to 5 reservations /month',
    starter: 'Up to 10 reservations /month',
    plus: 'Unlimited reservations',
  },
  [OfferCategory.food]: {
    basic: 'Up to 20 reservations /month',
    starter: 'Up to 50 reservations /month',
    plus: 'Unlimited reservations',
  },
  [OfferCategory.drink]: {
    basic: 'Up to 20 reservations /month',
    starter: 'Up to 50 reservations /month',
    plus: 'Unlimited reservations',
  },
  [OfferCategory.fitness]: {
    basic: 'Up to 20 reservations /month',
    starter: 'Up to 50 reservations /month',
    plus: 'Unlimited reservations',
  },
  [OfferCategory.experience]: {
    basic: 'Up to 20 reservations /month',
    starter: 'Up to 50 reservations /month',
    plus: 'Unlimited reservations',
  },
  // Default fallback for other types
  default: {
    basic: 'Up to 20 reservations /month',
    starter: 'Up to 50 reservations /month',
    plus: 'Unlimited reservations',
  },
};

export const getReservationLimit = (
  venueType: OfferCategory | null | undefined,
  plan: Plan,
): string => {
  // Avoid falling back to the generic defaults for event category.
  if (venueType === OfferCategory.event) {
    return 'No limited traffic';
  }

  const limits = venueTypeReservationLimits[venueType ?? ''] || venueTypeReservationLimits.default;
  return limits[plan];
};

export const getData = (venueType?: OfferCategory | null): Record<Plan, Data> => {
  const isEventVenue = venueType === OfferCategory.event;

  return {
    basic: {
      titleText: 'Free',
      title: 'Free',
      plan: 'basic',
      benefitTitle: <Box sx={{ fontWeight: 500, fontSize: '18px' }}>Basic usage of the platform</Box>,
      benefits: [
        ...(isEventVenue ? [] : [{ title: getReservationLimit(venueType, 'basic') }]),
        {
          title: 'Minimum followers filter up to 1k',
          tooltip: 'Minimum number of followers required for a user to book an offer',
        },
      ],
    },
    starter: {
      titleText: 'Starter',
      title: <Box sx={{ color: '#429C12' }}>Starter</Box>,
      highlight: (
        <Box
          sx={{
            background: '#429C12',
            color: 'white',
            borderRadius: 2,
            paddingX: 1.5,
            fontWeight: 400,
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          Popular
        </Box>
      ),
      plan: 'starter',
      benefitTitle: <span>Everything included in Free</span>,
      benefitColor: '#49B210',
      benefits: [
        ...(isEventVenue ? [] : [{ title: getReservationLimit(venueType, 'starter') }]),
        { title: 'Data & analytics' },
        {
          title: 'Minimum followers filter up to 10k',
          tooltip: 'Minimum number of followers required for a user to book an offer',
        },
        { title: 'Premium support' },
      ],
    },
    plus: {
      titleText: 'Beautypass PLUS',
      title: <Box sx={{ color: '#b084e7', fontWeight: 600 }}>PLUS</Box>,
      plan: 'plus',
      benefitTitle: (
        <>
          <span style={{ fontWeight: '500' }}>Everything included in</span>&nbsp;
          <Box sx={{ color: '#429C12', fontWeight: 'bold' }}>Starter</Box>
        </>
      ),
      benefitColor: '#B491D6',
      benefits: [
        ...(isEventVenue ? [] : [{ title: getReservationLimit(venueType, 'plus') }]),
        {
          title: 'Featured placement in app',
        },
        { title: 'Extra discovery tools' },
        { title: 'Unlimited filters' },
        { title: 'Top user promotions' },
      ],
    },
  };
};

// Backward compatible export for existing usage
export const data: Record<Plan, Data> = getData();

// "New prices" brand promotions (default)
export const brandDataNew: Record<BrandPlan, Data> = {
  basic: {
    titleText: 'Basic',
    title: 'Basic',
    plan: 'basic',
    benefitTitle: <Box sx={{ fontWeight: 500, fontSize: '18px' }}>Basic usage of the platform</Box>,
    benefits: [
      {
        title: (
          <span>
            <b>Up to 3 promotions /month</b>
          </span>
        ),
      },
      {
        title: 'Minimum followers up to 1k',
        tooltip: 'Minimum number of followers required for a user to book an offer',
      },
    ],
  },
  starter: {
    titleText: 'Starter',
    title: <Box sx={{ color: '#429C12' }}>Starter</Box>,
    highlight: (
      <Box
        sx={{
          background: '#429C12',
          color: 'white',
          borderRadius: 2,
          paddingX: 1.5,
          fontWeight: 400,
          display: 'flex',
          alignItems: 'center',
          fontSize: '1rem',
        }}
      >
        Popular
      </Box>
    ),
    plan: 'starter',
    benefitTitle: <span>Everything included in Basic</span>,
    benefitColor: '#49B210',
    benefits: [
      {
        title: (
          <span>
            <b>Up to 10 promotions /month</b>
          </span>
        ),
      },
      { title: 'Data & Analytics' },
      {
        title: 'Minimum followers up to 25k',
        tooltip: 'Minimum number of followers required for a user to book an offer',
      },
      { title: 'Filters when searching collabs' },
    ],
  },
  plus: {
    titleText: 'Beautypass PLUS',
    title: <Box sx={{ color: '#b084e7', fontWeight: 600 }}>PLUS</Box>,
    plan: 'plus',
    benefitTitle: (
      <>
        <span style={{ fontWeight: '500' }}>Everything included in</span>&nbsp;
        <Box sx={{ color: '#429C12', fontWeight: 'bold' }}>Starter</Box>
      </>
    ),
    benefitColor: '#B491D6',
    benefits: [
      {
        title: (
          <span>
            <b>Unlimited promotions</b>
          </span>
        ),
      },
      {
        title: (
          <span>
            <b>Top visibility</b> in app
          </span>
        ),
      },
      {
        title: (
          <span>
            <b>Unlimited</b> filters when creating offers
          </span>
        ),
      },
      {
        title: (
          <span>
            <b>Unlimited</b> filters when searching collabs
          </span>
        ),
      },
      {
        title: (
          <span>
            <b>Dedicated</b> account manager
          </span>
        ),
      },
    ],
  },
};

export const brandData: Record<BrandPlan, Data> = brandDataNew;

export const brandDataOld: Record<BrandPlan, Data> = {
  basic: {
    ...brandDataNew.basic,
    benefits: [
      {
        title: (
          <span>
            <b>Up to 5 promotions /month</b>
          </span>
        ),
      },
      brandDataNew.basic.benefits[1],
    ],
  },
  starter: {
    ...brandDataNew.starter,
    benefits: [
      {
        title: (
          <span>
            <b>Up to 25 promotions /month</b>
          </span>
        ),
      },
      brandDataNew.starter.benefits[1],
      brandDataNew.starter.benefits[2],
      brandDataNew.starter.benefits[3],
    ],
  },
  plus: brandDataNew.plus,
};

export const mapCondition: Record<string, LogicCondition[]> = {
  '0:0': [
    { label: 'Current Plan', kind: 'plan-cancel', selected: true },
    {
      label: 'Upgrade to Starter',
      kind: 'plan-upgrade',
      selected: false,
    },
    {
      label: 'Upgrade to PLUS',
      kind: 'plan-upgrade',
      selected: false,
    },
  ],
  '0:1': [
    { label: 'Downgrade to Free', kind: 'plan-downgrade', selected: false },
    { label: 'Current Plan', kind: 'plan-cancel', selected: true },
    { label: 'Upgrade to PLUS', kind: 'plan-upgrade', selected: false },
  ],
  '0:2': [
    { label: 'Downgrade to Free', kind: 'plan-downgrade', selected: false },
    {
      label: 'Downgrade to Starter',
      kind: 'plan-downgrade',
      selected: false,
    },
    { label: 'Current Plan', kind: 'plan-cancel', selected: true },
  ],
  '1:1': [
    { label: 'Downgrade to Free', kind: 'plan-downgrade', selected: false },
    { label: 'Increase billing period', kind: 'cycle-change-increase', selected: true },
    { label: 'Upgrade to PLUS', kind: 'plan-upgrade', selected: false },
  ],
  '1:2': [
    { label: 'Downgrade to Free', kind: 'plan-downgrade', selected: false },
    { label: 'Downgrade to Starter', kind: 'plan-downgrade', selected: false },
    { label: 'Increase billing period', kind: 'cycle-change-increase', selected: true },
  ],
  '2:1': [
    { label: 'Downgrade to Free', kind: 'plan-downgrade', selected: false },
    { label: 'Decrease billing period', kind: 'cycle-change-decrease', selected: true },
    { label: 'Upgrade to PLUS', kind: 'plan-upgrade', selected: false },
  ],
  '2:2': [
    { label: 'Downgrade to Free', kind: 'plan-downgrade', selected: false },
    { label: 'Downgrade to Starter', kind: 'plan-downgrade', selected: false },
    { label: 'Decrease billing period', kind: 'cycle-change-decrease', selected: true },
  ],
};

export const brandMapCondition: Record<string, LogicCondition[]> = {
  '0:-1': [
    { label: 'Subscribe to Basic', kind: 'plan-upgrade', selected: false },
    {
      label: 'Subscribe to Starter',
      kind: 'plan-upgrade',
      selected: false,
    },
    {
      label: 'Subscribe to PLUS',
      kind: 'plan-upgrade',
      selected: false,
    },
  ],
  '0:0': [
    { label: 'Current Plan', kind: 'plan-cancel', selected: true },
    {
      label: 'Subscribe to Starter',
      kind: 'plan-upgrade',
      selected: false,
    },
    {
      label: 'Subscribe to PLUS',
      kind: 'plan-upgrade',
      selected: false,
    },
  ],
  '0:1': [
    { label: 'Downgrade to Basic', kind: 'plan-downgrade', selected: false },
    { label: 'Current Plan', kind: 'plan-cancel', selected: true },
    { label: 'Subscribe to PLUS', kind: 'plan-upgrade', selected: false },
  ],
  '0:2': [
    { label: 'Downgrade to Basic', kind: 'plan-downgrade', selected: false },
    {
      label: 'Downgrade to Starter',
      kind: 'plan-downgrade',
      selected: false,
    },
    { label: 'Current Plan', kind: 'plan-cancel', selected: true },
  ],
  '1:1': [
    { label: 'Downgrade to Basic', kind: 'plan-downgrade', selected: false },
    { label: 'Increase billing period', kind: 'cycle-change-increase', selected: true },
    { label: 'Subscribe to PLUS', kind: 'plan-upgrade', selected: false },
  ],
  '1:2': [
    { label: 'Downgrade to Basic', kind: 'plan-downgrade', selected: false },
    { label: 'Downgrade to Starter', kind: 'plan-downgrade', selected: false },
    { label: 'Increase billing period', kind: 'cycle-change-increase', selected: true },
  ],
  '2:1': [
    { label: 'Downgrade to Basic', kind: 'plan-downgrade', selected: false },
    { label: 'Decrease billing period', kind: 'cycle-change-decrease', selected: true },
    { label: 'Subscribe to PLUS', kind: 'plan-upgrade', selected: false },
  ],
  '2:2': [
    { label: 'Downgrade to Basic', kind: 'plan-downgrade', selected: false },
    { label: 'Downgrade to Starter', kind: 'plan-downgrade', selected: false },
    { label: 'Decrease billing period', kind: 'cycle-change-decrease', selected: true },
  ],
};
